import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, X, GripVertical, Link, AlertCircle, MoreHorizontal, ChevronUp, ChevronLeft, ChevronRight, ChevronDown, DollarSign } from 'lucide-react';

const Cleaners = ({ cleaners: propCleaners = [], properties: propProperties = [], onCleanersChange }) => {
  const [cleaners, setCleaners] = useState(propCleaners);
  const [properties, setProperties] = useState(propProperties);
  const [availableProperties, setAvailableProperties] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCleaner, setCurrentCleaner] = useState(null);
  const [draggedCleaner, setDraggedCleaner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    assignedProperties: [] // Array of { propertyId, assignedDays }
  });
  const [expandedProperties, setExpandedProperties] = useState(new Set());

  // Monthly earnings modal state
  const [earningsCleaner, setEarningsCleaner] = useState(null); // cleaner whose earnings are shown
  const [earningsMonthOffset, setEarningsMonthOffset] = useState(0); // 0 = current month
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsError, setEarningsError] = useState(null);
  const [expandedEarningsRows, setExpandedEarningsRows] = useState(new Set());

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track in-flight requests to cancel on unmount
  const abortControllerRef = useRef(null);

  const DAYS_OF_WEEK = [
    { key: 'monday', label: 'Mon', fullLabel: 'Monday' },
    { key: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
    { key: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
    { key: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
    { key: 'friday', label: 'Fri', fullLabel: 'Friday' },
    { key: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
    { key: 'sunday', label: 'Sun', fullLabel: 'Sunday' }
  ];

  // Get current assignment for a property
  const getCurrentAssignment = (propertyId) => {
    return formData.assignedProperties.find(ap => ap.propertyId === propertyId);
  };

  // Get available days for a property from the fetched available properties
  const getAvailableDaysForProperty = (propertyId) => {
    const property = availableProperties.find(p => p._id === propertyId);
    return property ? property.availableDays : [];
  };

  // Sync with props when they change
  useEffect(() => {
    setCleaners(propCleaners);
  }, [propCleaners]);

  useEffect(() => {
    setProperties(propProperties);
  }, [propProperties]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Notify parent of changes
  const notifyCleanersChange = useCallback(() => {
    if (onCleanersChange) {
      onCleanersChange();
    }
  }, [onCleanersChange]);

  const fetchAvailableProperties = useCallback(async (cleanerId = null) => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const url = cleanerId
        ? `${process.env.REACT_APP_API_URL}/api/cleaners/available-properties?cleanerId=${cleanerId}`
        : `${process.env.REACT_APP_API_URL}/api/cleaners/available-properties`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setAvailableProperties(data);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') return;
      console.error('Error fetching available properties:', error);
    }
  }, []);

  const handleDragStart = (cleaner) => {
    setDraggedCleaner(cleaner);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (targetCleaner) => {
    if (!draggedCleaner || draggedCleaner._id === targetCleaner._id) return;

    try {
      setLoading(true);
      
      const updatedCleaners = [...cleaners];
      const draggedIndex = cleaners.findIndex(c => c._id === draggedCleaner._id);
      const targetIndex = cleaners.findIndex(c => c._id === targetCleaner._id);

      updatedCleaners.splice(draggedIndex, 1);
      updatedCleaners.splice(targetIndex, 0, draggedCleaner);

      const reorderedCleaners = updatedCleaners.map((cleaner, index) => ({
        ...cleaner,
        order: index
      }));

      setCleaners(reorderedCleaners);
      setDraggedCleaner(null);

      await Promise.all(reorderedCleaners.map(async (cleaner) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cleaners/${cleaner._id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: JSON.stringify(cleaner),
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update cleaner order: ${errorText}`);
        }
      }));

      // Refresh App's bootstrap cache so the new order survives a reload
      notifyCleanersChange();

    } catch (error) {
      console.error('Error updating cleaner orders:', error);
      if (isMountedRef.current) {
        setError(`Failed to reorder cleaners: ${error.message}`);
        // Revert to original order from props
        setCleaners(propCleaners);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAddCleaner = async () => {
    setCurrentCleaner(null);
    setFormData({
      name: '',
      assignedProperties: []
    });
    setExpandedProperties(new Set());
    await fetchAvailableProperties(); // Fetch without cleaner ID for new cleaner
    setIsModalOpen(true);
  };

  const handleEditCleaner = async (cleaner) => {
    setCurrentCleaner(cleaner);
    
    // Transform the cleaner's assigned properties to form data format
    const assignments = cleaner.assignedProperties?.map(ap => ({
      propertyId: ap.propertyId?._id || ap.propertyId,
      assignedDays: ap.assignedDays || []
    })) || [];

    setFormData({
      name: cleaner.name,
      assignedProperties: assignments
    });
    
    setExpandedProperties(new Set());
    await fetchAvailableProperties(cleaner._id); // Fetch excluding current cleaner
    setIsModalOpen(true);
  };

  const handleDeleteCleaner = async (cleanerId) => {
    if (!window.confirm('Are you sure you want to delete this cleaner?')) return;

    try {
      setLoading(true);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cleaners/${cleanerId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete cleaner: ${errorText}`);
      }

      // Optimistic update
      if (isMountedRef.current) {
        setCleaners(prev => prev.filter(c => c._id !== cleanerId));
      }

      // Notify parent to refresh
      notifyCleanersChange();
    } catch (error) {
      console.error('Error deleting cleaner:', error);
      if (isMountedRef.current) {
        setError(`Failed to delete cleaner: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Filter out assignments with no days selected
      const validAssignments = formData.assignedProperties.filter(ap => 
        ap.assignedDays && ap.assignedDays.length > 0
      );
      
      const payload = {
        name: formData.name,
        assignedProperties: validAssignments,
        order: currentCleaner ? currentCleaner.order : cleaners.length
      };
      
      console.log('Submitting cleaner:', payload);
      
      const url = currentCleaner 
        ? `${process.env.REACT_APP_API_URL}/api/cleaners/${currentCleaner._id}`
        : `${process.env.REACT_APP_API_URL}/api/cleaners`;
      
      const method = currentCleaner ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (isMountedRef.current) {
        setIsModalOpen(false);
      }

      // Notify parent to refresh cleaners list
      notifyCleanersChange();
    } catch (error) {
      console.error('Error saving cleaner:', error);
      if (isMountedRef.current) {
        setError(`Failed to save cleaner: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const togglePropertyAssignment = (propertyId) => {
    const existingIndex = formData.assignedProperties.findIndex(ap => ap.propertyId === propertyId);
    
    if (existingIndex >= 0) {
      // Remove assignment
      const updated = formData.assignedProperties.filter((_, index) => index !== existingIndex);
      setFormData({ ...formData, assignedProperties: updated });
    } else {
      // Add assignment with all available days
      const availableDays = getAvailableDaysForProperty(propertyId);
      const newAssignment = {
        propertyId,
        assignedDays: availableDays.slice() // Copy the array
      };
      setFormData({ 
        ...formData, 
        assignedProperties: [...formData.assignedProperties, newAssignment] 
      });
    }
  };

  const toggleDayAssignment = (propertyId, day) => {
    const assignments = [...formData.assignedProperties];
    const assignmentIndex = assignments.findIndex(ap => ap.propertyId === propertyId);
    
    if (assignmentIndex >= 0) {
      const assignment = assignments[assignmentIndex];
      const dayIndex = assignment.assignedDays.indexOf(day);
      
      if (dayIndex >= 0) {
        // Remove day
        assignment.assignedDays.splice(dayIndex, 1);
        if (assignment.assignedDays.length === 0) {
          // Remove entire assignment if no days selected
          assignments.splice(assignmentIndex, 1);
        }
      } else {
        // Add day
        assignment.assignedDays.push(day);
      }
      
      setFormData({ ...formData, assignedProperties: assignments });
    }
  };

  const togglePropertyExpansion = (propertyId) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyId)) {
      newExpanded.delete(propertyId);
    } else {
      newExpanded.add(propertyId);
    }
    setExpandedProperties(newExpanded);
  };

  // Helper to generate a URL-safe cleaner link
  const generateCleanerLink = (name) => {
    try {
      return btoa(name.replace(/\s+/g, '-').toLowerCase());
    } catch (e) {
      // btoa throws on non-Latin1 characters (e.g. accented names) —
      // the Calendar route decodes with plain atob, so skip the link
      // rather than crash the table render
      console.error('Cannot generate calendar link for name:', name, e);
      return null;
    }
  };

  // --- Monthly earnings helpers ---
  const getEarningsMonthDate = (offset) => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    d.setMonth(d.getMonth() + offset);
    return d;
  };

  const getEarningsMonthKey = (offset) => {
    const d = getEarningsMonthDate(offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatEarningsMonthLabel = (offset) => {
    return getEarningsMonthDate(offset).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const openEarningsModal = (cleaner) => {
    setEarningsCleaner(cleaner);
    setEarningsMonthOffset(0);
    setEarningsData(null);
    setEarningsError(null);
    setExpandedEarningsRows(new Set());
  };

  const closeEarningsModal = () => {
    setEarningsCleaner(null);
    setEarningsData(null);
    setEarningsError(null);
  };

  const toggleEarningsRow = (propertyName) => {
    setExpandedEarningsRows(prev => {
      const next = new Set(prev);
      if (next.has(propertyName)) {
        next.delete(propertyName);
      } else {
        next.add(propertyName);
      }
      return next;
    });
  };

  // Fetch earnings whenever the modal opens or the month changes
  useEffect(() => {
    if (!earningsCleaner) return;

    const controller = new AbortController();
    const monthKey = getEarningsMonthKey(earningsMonthOffset);

    const fetchEarnings = async () => {
      try {
        setEarningsLoading(true);
        setEarningsError(null);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cleaners/monthly-earnings?month=${monthKey}&cleanerId=${earningsCleaner._id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        if (isMountedRef.current) {
          setEarningsData(data);
          setExpandedEarningsRows(new Set());
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error fetching monthly earnings:', err);
        if (isMountedRef.current) {
          setEarningsError(err.message);
        }
      } finally {
        if (isMountedRef.current) {
          setEarningsLoading(false);
        }
      }
    };

    fetchEarnings();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningsCleaner, earningsMonthOffset]);

  const formatCheckoutDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPropertyAssignments = (assignments) => {
    if (!assignments || assignments.length === 0) {
      return <span className="text-sm text-gray-500">No properties assigned</span>;
    }

    return assignments.map(assignment => {
      const property = properties.find(p => 
        p._id === (assignment.propertyId?._id || assignment.propertyId)
      );
      
      if (!property) return null;

      const assignedDaysCount = assignment.assignedDays?.length || 0;
      const dayLabels = assignedDaysCount === 7 
        ? 'All days' 
        : assignment.assignedDays?.map(day => 
            DAYS_OF_WEEK.find(d => d.key === day)?.label
          ).filter(Boolean).join(', ') || 'No days';

      const isPartial = assignedDaysCount < 7 && assignedDaysCount > 0;

      return (
        <div key={property._id} className="mb-1">
          <div className="flex items-center gap-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              isPartial ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800' : 'bg-violet-900/30 text-violet-400 border border-violet-800'
            }`}>
              {property.name}
              {isPartial && <span className="ml-1">*</span>}
            </span>
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">{dayLabels}</div>
        </div>
      );
    }).filter(Boolean);
  };

  // Show error state
  if (error && !isModalOpen) {
    return (
      <div className="h-full flex flex-col bg-zinc-900">
        <div className="border-b border-zinc-800 p-4 flex justify-between items-center">
          <h2 className="text-base font-semibold text-zinc-100">Cleaners</h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="bg-red-950/50 border border-red-800 rounded-xl p-6 max-w-md">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <h3 className="text-base font-medium text-red-300 mb-2">Database Error</h3>
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  notifyCleanersChange();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex flex-col bg-zinc-900 min-h-full">
        {/* Header */}
        <div className="border-b border-zinc-800 p-4 flex justify-end items-center">
          <button
            onClick={handleAddCleaner}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 transition-colors"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </button>
        </div>

        {/* Loading State */}
        {loading && !isModalOpen && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto"></div>
            <p className="mt-2 text-zinc-500 text-sm">Loading...</p>
          </div>
        )}

        {/* Error Banner */}
        {error && isModalOpen && (
          <div className="mx-4 mt-4 p-3 bg-red-950/50 border border-red-800 rounded-lg">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-zinc-800">
            <thead className="bg-zinc-950">
              <tr>
                <th scope="col" className="w-10 px-2 py-3"></th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Properties & Days
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-zinc-900 divide-y divide-zinc-800">
              {[...cleaners].sort((a, b) => a.order - b.order).map((cleaner) => (
                <tr
                  key={cleaner._id}
                  draggable={!loading}
                  onDragStart={() => handleDragStart(cleaner)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(cleaner)}
                  className="hover:bg-zinc-800/50 transition-colors"
                >
                  <td className="px-2 py-4">
                    <GripVertical className="w-4 h-4 text-zinc-600 cursor-move" />
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm font-medium text-zinc-200">
                      {cleaner.name}
                    </div>
                    {/* Mobile view: show properties below name */}
                    <div className="md:hidden mt-2">
                      {formatPropertyAssignments(cleaner.assignedProperties)}
                    </div>
                  </td>
                  {/* Desktop view: dedicated column for properties */}
                  <td className="hidden md:table-cell px-3 py-4">
                    {formatPropertyAssignments(cleaner.assignedProperties)}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-1 sm:gap-2">
                      <button
                        onClick={() => openEarningsModal(cleaner)}
                        disabled={loading}
                        className="text-zinc-500 hover:text-emerald-400 disabled:opacity-50 p-1 transition-colors"
                        title="View monthly earnings"
                      >
                        <DollarSign className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditCleaner(cleaner)}
                        disabled={loading}
                        className="text-zinc-500 hover:text-violet-400 disabled:opacity-50 p-1 transition-colors"
                        title="Edit cleaner"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCleaner(cleaner._id)}
                        disabled={loading}
                        className="text-zinc-500 hover:text-red-400 disabled:opacity-50 p-1 transition-colors"
                        title="Delete cleaner"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {generateCleanerLink(cleaner.name) && (
                        <a
                          href={`/calendar/${generateCleanerLink(cleaner.name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-zinc-500 hover:text-emerald-400 p-1 transition-colors"
                          title="View calendar"
                        >
                          <Link className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {cleaners.length === 0 && !loading && (
                <tr>
                  <td colSpan="4" className="px-3 py-8 text-center text-zinc-500 text-sm">
                    No cleaners found. Click "Add Cleaner" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Earnings Modal */}
      {earningsCleaner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-zinc-800 flex justify-between items-center sticky top-0 bg-zinc-900 z-10">
              <div>
                <h3 className="text-base font-semibold text-zinc-100">Monthly Earnings</h3>
                <p className="text-sm text-zinc-500">{earningsCleaner.name}</p>
              </div>
              <button
                onClick={closeEarningsModal}
                className="text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Month navigation */}
            <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between">
              <button
                onClick={() => setEarningsMonthOffset(prev => prev - 1)}
                disabled={earningsLoading}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                title="Previous month"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-sm font-medium text-zinc-200">
                {formatEarningsMonthLabel(earningsMonthOffset)}
                {earningsMonthOffset === 0 && (
                  <span className="ml-2 text-xs text-violet-400">(current)</span>
                )}
              </div>
              <button
                onClick={() => setEarningsMonthOffset(prev => prev + 1)}
                disabled={earningsLoading}
                className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                title="Next month"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {earningsLoading && (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto"></div>
                  <p className="mt-2 text-zinc-500 text-sm">Loading earnings...</p>
                </div>
              )}

              {earningsError && !earningsLoading && (
                <div className="p-3 bg-red-950/50 border border-red-800 rounded-lg flex">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="ml-3 text-sm text-red-400">{earningsError}</p>
                </div>
              )}

              {earningsData && !earningsLoading && !earningsError && (
                <div className="space-y-4">
                  {/* Total card */}
                  <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-center">
                    <div className="text-3xl font-semibold text-emerald-400">
                      ${earningsData.total.toFixed(2)}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      {earningsData.totalCheckouts} checkout{earningsData.totalCheckouts !== 1 ? 's' : ''}
                      {earningsData.notesTotal !== 0 && (
                        <> · ${earningsData.checkoutTotal.toFixed(2)} cleanings + ${earningsData.notesTotal.toFixed(2)} adjustments</>
                      )}
                    </div>
                  </div>

                  {/* Per-property breakdown */}
                  {earningsData.breakdown.length > 0 ? (
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
                      <div className="px-4 py-2 border-b border-zinc-800 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                        By Property
                      </div>
                      {earningsData.breakdown.map(item => {
                        const isExpanded = expandedEarningsRows.has(item.propertyName);
                        return (
                          <div key={item.propertyName} className="border-b border-zinc-800/50 last:border-b-0">
                            <button
                              onClick={() => toggleEarningsRow(item.propertyName)}
                              className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-zinc-900/70 transition-colors"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {isExpanded ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" />
                                )}
                                <span className="text-sm text-zinc-300 truncate">{item.propertyName}</span>
                                <span className="text-xs text-zinc-600 flex-shrink-0">
                                  ×{item.checkouts}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-zinc-200 ml-3 flex-shrink-0">
                                ${item.amount.toFixed(2)}
                              </span>
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-2.5 pl-10">
                                <div className="flex flex-wrap gap-1.5">
                                  {item.checkoutDates.map((date, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex px-2 py-0.5 rounded-full text-xs bg-zinc-800 text-zinc-400 border border-zinc-700"
                                    >
                                      {formatCheckoutDate(date)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500 text-center py-2">
                      No checkouts this month
                    </p>
                  )}

                  {/* Weekly notes / adjustments */}
                  {earningsData.notes.length > 0 && (
                    <div className="bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden">
                      <div className="px-4 py-2 border-b border-zinc-800 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                        Adjustments (weekly notes)
                      </div>
                      {earningsData.notes.map((note, i) => (
                        <div key={i} className="px-4 py-2.5 flex items-center justify-between border-b border-zinc-800/50 last:border-b-0">
                          <div className="min-w-0">
                            <span className="text-sm text-zinc-300">{note.name || 'Adjustment'}</span>
                            <span className="ml-2 text-xs text-zinc-600">
                              week of {formatCheckoutDate(note.weekStart)}
                            </span>
                          </div>
                          <span className={`text-sm font-medium ml-3 flex-shrink-0 ${note.amount < 0 ? 'text-red-400' : 'text-zinc-200'}`}>
                            {note.amount < 0 ? '-' : ''}${Math.abs(note.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-zinc-600 text-center">
                    Based on accepted reservations checking out {formatEarningsMonthLabel(earningsMonthOffset)},
                    including temporary reassignments. Adjustments are weekly notes counted in the month their week ends in.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Modal with Day Selection */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-zinc-800 flex justify-between items-center sticky top-0 bg-zinc-900">
              <h3 className="text-base font-semibold text-zinc-100">
                {currentCleaner ? 'Edit Cleaner' : 'Add Cleaner'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
                className="text-zinc-500 hover:text-zinc-300 disabled:opacity-50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-zinc-400 mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full bg-zinc-800 border border-zinc-700 rounded-lg py-2 px-3 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-zinc-400 mb-2">
                    Property Assignments
                  </label>
                  <div className="space-y-3 max-h-64 overflow-auto bg-zinc-950 border border-zinc-800 rounded-lg p-3">
                    {availableProperties.map(property => {
                      const currentAssignment = getCurrentAssignment(property._id);
                      const availableDays = property.availableDays || [];
                      const isAssigned = !!currentAssignment;
                      const isExpanded = expandedProperties.has(property._id);
                      const assignedDaysCount = currentAssignment?.assignedDays?.length || 0;

                      return (
                        <div key={property._id} className="border-b border-zinc-800 pb-3 last:border-b-0">
                          <div className="flex items-center justify-between">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={() => togglePropertyAssignment(property._id)}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-zinc-600 rounded bg-zinc-800"
                                disabled={loading}
                              />
                              <span className="text-sm font-medium text-zinc-300">{property.name}</span>
                              {isAssigned && (
                                <span className="text-xs text-zinc-600">
                                  ({assignedDaysCount === 7 ? 'All days' : `${assignedDaysCount} days`})
                                </span>
                              )}
                            </label>
                            <div className="flex items-center gap-2">
                              {availableDays.length < DAYS_OF_WEEK.length && (
                                <span className="text-xs text-zinc-600">
                                  {DAYS_OF_WEEK.length - availableDays.length} days unavailable
                                </span>
                              )}
                              {isAssigned && (
                                <button
                                  type="button"
                                  onClick={() => togglePropertyExpansion(property._id)}
                                  className="text-zinc-500 hover:text-zinc-300 p-1 transition-colors"
                                  title="Configure days"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <MoreHorizontal className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          {isAssigned && isExpanded && (
                            <div className="ml-6 mt-3 p-3 bg-zinc-900 rounded-lg border border-zinc-800">
                              <div className="text-xs text-zinc-500 mb-2">Assign days:</div>
                              <div className="grid grid-cols-7 gap-1">
                                {DAYS_OF_WEEK.map(day => {
                                  const isAvailable = availableDays.some(ad => ad === day.key);
                                  const isSelected = currentAssignment?.assignedDays?.includes(day.key);

                                  return (
                                    <button
                                      key={day.key}
                                      type="button"
                                      onClick={() => isAvailable && toggleDayAssignment(property._id, day.key)}
                                      disabled={!isAvailable || loading}
                                      className={`
                                        text-xs py-1 px-1 rounded text-center transition-colors
                                        ${isSelected
                                          ? 'bg-violet-600 text-white'
                                          : isAvailable
                                            ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                                            : 'bg-zinc-800 text-zinc-600 cursor-not-allowed border border-zinc-800'
                                        }
                                      `}
                                      title={isAvailable ? day.fullLabel : `${day.fullLabel} - assigned to another cleaner`}
                                    >
                                      {day.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="mt-2 flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const assignments = [...formData.assignedProperties];
                                    const assignmentIndex = assignments.findIndex(ap => ap.propertyId === property._id);
                                    if (assignmentIndex >= 0) {
                                      assignments[assignmentIndex].assignedDays = availableDays.slice();
                                      setFormData({ ...formData, assignedProperties: assignments });
                                    }
                                  }}
                                  className="text-xs px-2 py-1 text-violet-400 hover:text-violet-300 transition-colors"
                                >
                                  Select All
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const assignments = [...formData.assignedProperties];
                                    const assignmentIndex = assignments.findIndex(ap => ap.propertyId === property._id);
                                    if (assignmentIndex >= 0) {
                                      assignments[assignmentIndex].assignedDays = [];
                                      setFormData({ ...formData, assignedProperties: assignments });
                                    }
                                  }}
                                  className="text-xs px-2 py-1 text-zinc-500 hover:text-zinc-300 transition-colors"
                                >
                                  Clear All
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {availableProperties.length === 0 && (
                      <p className="text-sm text-zinc-500 text-center py-4">
                        No properties available for assignment
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 border border-zinc-700 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Saving...' : (currentCleaner ? 'Save Changes' : 'Add Cleaner')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cleaners;